/**
 * Collections Hooks
 * React Query hooks for collections API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getCollections,
    getCollection,
    createCollection,
    updateCollection,
    deleteCollection,
    renameFolder,
    type Collection,
    type CreateCollectionPayload,
    type UpdateCollectionPayload,
} from '@/api/collections';
import { toast } from 'sonner';

// Re-export types
export type { Collection, CreateCollectionPayload, UpdateCollectionPayload };

// Query keys
export const collectionsKeys = {
    all: ['collections'] as const,
    list: () => [...collectionsKeys.all, 'list'] as const,
    detail: (collection: string) => [...collectionsKeys.all, 'detail', collection] as const,
};

// ============== Queries ==============

export function useCollections() {
    return useQuery({
        queryKey: collectionsKeys.list(),
        queryFn: getCollections,
    });
}

export function useCollection(collection: string) {
    return useQuery({
        queryKey: collectionsKeys.detail(collection),
        queryFn: () => getCollection(collection),
        enabled: !!collection,
    });
}

// ============== Mutations ==============

export function useCreateCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: CreateCollectionPayload) => createCollection(payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: collectionsKeys.list() });
            toast.success(`Collection "${data.collection}" created`);
        },
        onError: (error: Error) => {
            toast.error(`Failed to create collection: ${error.message}`);
        },
    });
}

export function useUpdateCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ collection, payload }: { collection: string; payload: UpdateCollectionPayload }) =>
            updateCollection(collection, payload),
        onSuccess: (_, { collection }) => {
            queryClient.invalidateQueries({ queryKey: collectionsKeys.list() });
            queryClient.invalidateQueries({ queryKey: collectionsKeys.detail(collection) });
        },
        onError: (error: Error) => {
            toast.error(`Failed to update collection: ${error.message}`);
        },
    });
}

export function useDeleteCollection() {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: (collection: string) => deleteCollection(collection),
        onSuccess: (_, collection) => {
            queryClient.invalidateQueries({ queryKey: collectionsKeys.list() });
            queryClient.removeQueries({ queryKey: collectionsKeys.detail(collection) });
            toast.success('Collection deleted');
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete collection: ${error.message}`);
        },
    });
}

export function useRenameFolder() {
    const queryClient = useQueryClient();

    return useMutation<Collection, Error, { collection: string; newName: string }>({
        mutationFn: ({ collection, newName }) => renameFolder(collection, newName),
        onSuccess: (data, { collection: oldName }) => {
            queryClient.invalidateQueries({ queryKey: collectionsKeys.list() });
            queryClient.removeQueries({ queryKey: collectionsKeys.detail(oldName) });
            queryClient.invalidateQueries({ queryKey: collectionsKeys.detail(data.collection) });
            toast.success(`Folder renamed to "${data.collection}"`);
        },
        onError: (error: Error) => {
            toast.error(`Failed to rename folder: ${error.message}`);
        },
    });
}

// ============== Utilities ==============

/**
 * Filter collections by type
 */
export function filterCollections(collections: Collection[], filter: 'user' | 'system' | 'all' = 'all') {
    if (filter === 'all') return collections;

    return collections.filter((c) => {
        const isSystem = c.collection.startsWith('cms_');
        return filter === 'system' ? isSystem : !isSystem;
    });
}

/**
 * Sort collections alphabetically, with system collections at the end
 */
export function sortCollections(collections: Collection[]) {
    return [...collections].sort((a, b) => {
        const aIsSystem = a.collection.startsWith('cms_');
        const bIsSystem = b.collection.startsWith('cms_');

        if (aIsSystem && !bIsSystem) return 1;
        if (!aIsSystem && bIsSystem) return -1;

        return a.collection.localeCompare(b.collection);
    });
}

/**
 * Check if collection is a system collection
 */
export function isSystemCollection(collection: Collection | string): boolean {
    const name = typeof collection === 'string' ? collection : collection.collection;
    return name.startsWith('cms_');
}
